const esc = (v) =>
  String(v ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[c]);

export const money = (amount) => {
  const n = Math.round(Number(amount) || 0);
  return `${n < 0 ? "-" : ""}${Math.abs(n).toLocaleString("en-IN")}`;
};

export const receiptSlipCss = (primary = "#1976d2") => `
  /* Continuous single page: A5 width with tall height so the receipt does not paginate */
  @page {
    size: 148mm 600mm;
    margin: 6mm;
  }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body {
    height: auto !important;
    overflow: visible !important;
  }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
    padding: 16px;
    font-size: 14px;
    color: #0f172a;
    background: #eef2f7;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .slip {
    max-width: 480px;
    margin: auto;
    background: #fff;
    padding: 20px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 3px solid ${primary};
  }
  .brand { display: flex; align-items: center; gap: 10px; min-width: 0; }
  .brand img { width: 46px; height: 46px; object-fit: contain; }
  .company { color: ${primary}; font-weight: 800; font-size: 18px; line-height: 1.2; }
  .branch { color: #475569; font-size: 13px; margin-top: 3px; font-weight: 600; }
  .proprietor { color: #64748b; font-size: 11px; margin-top: 4px; line-height: 1.35; }
  .hdate {
    text-align: right;
    color: #475569;
    font-size: 12px;
    white-space: nowrap;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 8px 10px;
  }
  .hdate strong { display: block; margin-top: 2px; font-size: 14px; color: #0f172a; }
  .meta {
    padding: 12px 0;
    border-bottom: 1px solid #e2e8f0;
    margin-bottom: 12px;
  }
  .customer { font-size: 18px; font-weight: 800; text-transform: capitalize; color: #0f172a; }
  .location { color: #64748b; font-size: 13px; margin-top: 4px; text-transform: capitalize; }
  @media print {
    body { padding: 0; background: #fff; height: auto !important; overflow: visible !important; }
    .slip {
      border: none;
      max-width: none;
      width: 100%;
      padding: 0;
      box-shadow: none;
      border-radius: 0;
    }
    /* Keep each section together, but do not force a new page */
    table.rcpt-section-table {
      page-break-inside: avoid;
      break-inside: avoid;
      page-break-after: auto;
      break-after: auto;
    }
  }
`;

export const receiptTableCss = (primary = "#1976d2", success = "#16a34a", danger = "#dc2626") => `
  .rcpt-tables-container {
    width: 100%;
    font-size: 14px;
    color: #0f172a;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  table.rcpt-section-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    margin-bottom: 12px;
    font-size: 14px;
    color: #0f172a;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
  }
  table.rcpt-section-table td, table.rcpt-section-table th {
    padding: 8px 10px;
    vertical-align: middle;
  }

  .sec-header {
    font-size: 12px;
    font-weight: 800;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    text-align: left;
    padding: 8px 10px !important;
    background: ${primary};
  }

  /* Column widths for 3-col tables (Sales) */
  .item-col { width: 46%; padding-right: 8px; text-align: left; overflow-wrap: anywhere; font-weight: 600; }
  .calc-col { width: 28%; text-align: right; white-space: nowrap; color: #475569; font-size: 13px; padding-right: 12px; }
  .amt-col { width: 26%; text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; font-weight: 700; font-size: 14px; }

  /* Opening Balance Table */
  table.opening-table {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
  table.opening-table td {
    font-size: 15px;
    font-weight: 800;
    color: #334155;
    padding: 10px;
  }

  /* Sales Table */
  table.sales-table {
    background: #f0f7ff;
    border-color: #bfdbfe;
  }
  table.sales-table .sec-header { background: #2563eb; }
  table.sales-table .item-row td {
    padding: 7px 10px;
    border-top: 1px solid #dbeafe;
  }
  table.sales-table .total-row td {
    border-top: 2px solid #93c5fd;
    padding-top: 9px !important;
    padding-bottom: 9px !important;
    font-size: 15px;
    font-weight: 800;
    color: #0f172a;
    background: #dbeafe;
  }

  /* Discount Table (amount taken off the bill) */
  table.discount-table {
    background: #faf5ff;
    border-color: #d8b4fe;
  }
  table.discount-table .sec-header { background: #7c3aed; }
  table.discount-table td {
    font-size: 15px;
    font-weight: 800;
    color: #5b21b6;
    padding: 10px;
  }
  table.discount-table .discount-note {
    font-size: 11px;
    font-weight: 600;
    color: #7c3aed;
    margin-top: 2px;
  }
  .discount-amt {
    color: #7c3aed;
    font-variant-numeric: tabular-nums;
  }

  /* Payment Table */
  table.payment-table {
    background: #f0fdf4;
    border-color: #86efac;
  }
  table.payment-table .sec-header { background: #16a34a; }
  table.payment-table td {
    font-size: 15px;
    font-weight: 800;
    color: #14532d;
    padding: 10px;
  }

  /* Tray Table */
  table.tray-table {
    background: #fff7ed;
    border-color: #fdba74;
  }
  table.tray-table .sec-header { background: #ea580c; }
  table.tray-table .item-row td {
    border-top: 1px solid #ffedd5;
  }
  table.tray-table .tray-col {
    width: 38%;
    padding-right: 8px;
    text-align: left;
    overflow-wrap: anywhere;
    font-weight: 600;
  }
  table.tray-table .tray-calc-col {
    width: 36%;
    text-align: right;
    white-space: nowrap;
    font-size: 13px;
    color: #9a3412;
    padding-right: 12px;
  }
  table.tray-table .tray-total-cell {
    width: 26%;
    text-align: right;
    vertical-align: middle;
    font-size: 16px;
    font-weight: 800;
    color: ${success};
    border-left: 1px solid #fdba74;
    background: #ffedd5;
    padding-left: 8px !important;
    padding-right: 10px !important;
  }
  .tray-total-sub {
    font-size: 11px;
    font-weight: 500;
    color: #64748b;
    margin-top: 2px;
  }

  /* Due Balance Table */
  table.due-table {
    border: 2px solid ${primary};
    background: #eff6ff;
    margin-top: 4px;
    margin-bottom: 0;
  }
  table.due-table td {
    padding: 12px 10px;
    font-size: 18px;
    font-weight: 800;
    color: ${primary};
  }
  .final-label {
    text-align: left;
    font-size: 17px;
    font-weight: 800;
    color: ${primary};
  }
  .final-amt {
    text-align: right;
    font-size: 20px;
    font-weight: 800;
    color: ${primary};
    font-variant-numeric: tabular-nums;
  }

  .credit { color: ${success}; }
  .bold { font-weight: 800; }
  .nocost { font-size: 12px; font-weight: 700; color: #64748b; }
`;

export const buildReceiptTable = ({
  openingBalance = 0,
  sales = [],
  loadingCharges = null,
  damageRefunds = [],
  discount = 0,
  subTotal,
  paymentAmount = 0,
  trays = [],
  trayPaymentTotal,
  closingBalance = 0,
}) => {
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  // 1. Opening Balance Table
  const openingTable = `
    <table class="rcpt-section-table opening-table">
      <tbody>
        <tr>
          <td class="item-col bold">Opening Balance</td>
          <td colspan="2" class="amt-col bold">${money(openingBalance)}</td>
        </tr>
      </tbody>
    </table>`;

  // 2. Sales Items
  const salesRows = (sales || [])
    .map((s) => {
      const calc =
        s.qty != null && s.rate != null
          ? `${esc(s.qty)} X ${money(s.rate)}`
          : "";
      return `
      <tr class="item-row">
        <td class="item-col">${esc(s.name || s.item || "Item")}</td>
        <td class="calc-col">${calc}</td>
        <td class="amt-col">${money(s.amount)}</td>
      </tr>`;
    })
    .join("");

  // Loading Charges
  const loadingTotal = loadingCharges
    ? num(loadingCharges.total || loadingCharges.amount)
    : 0;
  const loadingQty = loadingCharges
    ? loadingCharges.qty ?? loadingCharges.numberOfItems
    : null;
  const loadingRate = loadingCharges
    ? loadingCharges.rate ?? loadingCharges.costPerItem
    : null;
  const loadingRow =
    loadingTotal > 0
      ? `
      <tr class="item-row">
        <td class="item-col">Loading Charges</td>
        <td class="calc-col">${
          loadingQty != null && loadingRate != null
            ? `${esc(loadingQty)} X ${money(loadingRate)}`
            : ""
        }</td>
        <td class="amt-col">${money(loadingTotal)}</td>
      </tr>`
      : "";

  // Damage Refunds / Returns
  const refundRows = (damageRefunds || [])
    .map((r) => {
      const rAmt = num(r.total || r.amount);
      const rQty = r.qty ?? r.numberOfItems;
      const rRate = r.rate ?? r.costPerItem;
      const calc =
        rQty != null && rRate != null
          ? `${esc(rQty)} X ${money(rRate)}`
          : "";
      const label =
        r.itemName || r.name || r.item
          ? `Return (${r.itemName || r.name || r.item})`
          : "Return";
      return `
      <tr class="item-row">
        <td class="item-col">${esc(label)}</td>
        <td class="calc-col">${calc}</td>
        <td class="amt-col">${money(Math.abs(rAmt))}</td>
      </tr>`;
    })
    .join("");

  // Discount (separate section — amount taken off the bill)
  const discountVal = num(discount);

  // Sales total before discount (Opening + Sales + Loading − Returns)
  const salesItemsTotal = (sales || []).reduce(
    (sum, s) => sum + num(s.amount),
    0
  );
  const refundTotal = (damageRefunds || []).reduce(
    (sum, r) => sum + num(r.total || r.amount),
    0
  );
  const salesBeforeDiscount =
    salesItemsTotal + loadingTotal - refundTotal;
  const computedSalesTotal =
    subTotal != null
      ? num(subTotal) + discountVal
      : num(openingBalance) + salesBeforeDiscount;

  const hasSales =
    (Array.isArray(sales) && sales.length > 0) ||
    loadingTotal > 0 ||
    (Array.isArray(damageRefunds) && damageRefunds.length > 0);

  const salesTable = hasSales
    ? `
    <table class="rcpt-section-table sales-table">
      <thead>
        <tr>
          <th colspan="3" class="sec-header">Sales</th>
        </tr>
      </thead>
      <tbody>
        ${salesRows}
        ${loadingRow}
        ${refundRows}
      </tbody>
      <tfoot>
        <tr class="total-row">
          <td colspan="2" class="bold">Total</td>
          <td class="amt-col bold">${money(computedSalesTotal)}</td>
        </tr>
      </tfoot>
    </table>`
    : "";

  // Discount Table (only when discount is given)
  const discountTable =
    discountVal > 0
      ? `
    <table class="rcpt-section-table discount-table">
      <thead>
        <tr>
          <th colspan="3" class="sec-header">Discount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="item-col bold">
            Discount
            <div class="discount-note">Taken off from bill</div>
          </td>
          <td colspan="2" class="amt-col discount-amt">− ${money(discountVal)}</td>
        </tr>
      </tbody>
    </table>`
      : "";

  // Payment Table
  const paymentVal = num(paymentAmount);
  const paymentTable =
    paymentVal > 0
      ? `
    <table class="rcpt-section-table payment-table">
      <thead>
        <tr>
          <th colspan="3" class="sec-header">Payment</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="item-col bold">Cash / Online Payment</td>
          <td colspan="2" class="amt-col bold credit">${money(paymentVal)}</td>
        </tr>
      </tbody>
    </table>`
      : "";

  // 4. Tray Payment Table
  const hasTrays = Array.isArray(trays) && trays.length > 0;
  const allTraysExcluded =
    hasTrays && trays.every((t) => t.excluded || t.excludePayment);
  const computedTrayTotal =
    trayPaymentTotal != null
      ? num(trayPaymentTotal)
      : (trays || []).reduce(
          (sum, t) =>
            sum +
            (t.excluded || t.excludePayment
              ? 0
              : num(
                  t.amount ||
                    num(t.qty || t.numberOfTrays) *
                      num(t.rate || t.costPerTray)
                )),
          0
        );

  const trayRows = hasTrays
    ? trays
        .map((t, idx) => {
          const qty = t.qty ?? t.numberOfTrays ?? 0;
          const rate = t.rate ?? t.costPerTray ?? 0;
          const isEx = !!(t.excluded || t.excludePayment);
          const lineTotal = isEx ? 0 : num(t.amount || num(qty) * num(rate));
          const name = t.name || t.trayName || t.item || "Tray";
          const formulaWithTotal = isEx
            ? `${esc(qty)} X ${money(rate)} = <span class="nocost">No Cost</span>`
            : `${esc(qty)} X ${money(rate)} = ${money(lineTotal)}`;

          const totalCell =
            idx === 0
              ? `<td rowspan="${trays.length}" class="tray-total-cell">
                  ${
                    allTraysExcluded
                      ? '<span class="nocost">No Cost</span>'
                      : money(computedTrayTotal)
                  }
                  <div class="tray-total-sub"></div>
                </td>`
              : "";

          return `
          <tr class="item-row">
            <td class="tray-col">${esc(name)}</td>
            <td class="tray-calc-col">${formulaWithTotal}</td>
            ${totalCell}
          </tr>`;
        })
        .join("")
    : "";

  const trayTable = hasTrays
    ? `
    <table class="rcpt-section-table tray-table">
      <thead>
        <tr>
          <th colspan="3" class="sec-header">Tray Payment</th>
        </tr>
      </thead>
      <tbody>
        ${trayRows}
      </tbody>
    </table>`
    : "";

  // 5. Due Balance Table
  const dueTable = `
    <table class="rcpt-section-table due-table">
      <tbody>
        <tr>
          <td class="final-label bold">Due Balance</td>
          <td colspan="2" class="final-amt bold">${money(closingBalance)}</td>
        </tr>
      </tbody>
    </table>`;

  return `
    <div class="rcpt-tables-container">
      ${openingTable}
      ${salesTable}
      ${discountTable}
      ${paymentTable}
      ${trayTable}
      ${dueTable}
    </div>`;
};
